import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import styles from "./AdminAssessments.module.css";
import { adminListQuizSubmissions } from "../../../../lib/store";
import quiz from "../../../../json/Quiz.json";

const PER_PAGE = 10;

function EyeIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

// ── Stat card icons ──
function IconStack() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 2 2 7l10 5 10-5-10-5z" />
      <path d="m2 17 10 5 10-5" />
      <path d="m2 12 10 5 10-5" />
    </svg>
  );
}
function IconFlame() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z" />
    </svg>
  );
}
function IconGauge() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="m12 14 4-4" />
      <path d="M3.34 19a10 10 0 1 1 17.32 0" />
    </svg>
  );
}
function IconTrendDown() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="22 17 13.5 8.5 8.5 13.5 2 7" />
      <polyline points="16 17 22 17 22 11" />
    </svg>
  );
}

const READINESS_GROUPS = (
  quiz.sections
    .flatMap((s) => s.questions)
    .find((q) => Array.isArray(q.groups)) || {}
).groups?.map((g) => g.label) || [];

// A grouped answer (Q7) is an object keyed by group index with no own `label`.
const isGrouped = (a) =>
  a && typeof a === "object" && !a.label && Object.keys(a).length > 0;

const fmtDateTime = (iso) => {
  if (!iso) return "N/A";
  try {
    return new Date(iso).toLocaleString("en-US", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
};

// Grade visuals — theme cyan (the grade letter itself differentiates).
const GRADE_COLOR = { A: "#00a7e1", B: "#00a7e1", C: "#00a7e1", D: "#00a7e1" };
const GRADE_WORD = {
  A: "Hot",
  B: "Warm: prioritize",
  C: "Develop: nurture",
  D: "Low: light-touch",
};
const pct = (n, max) =>
  `${Math.max(0, Math.min(100, (Number(n || 0) / max) * 100))}%`;

// Dropout Reason is derived from Q4 (Fitting Friction) — each option carries an
// `insight` label in Quiz.json. Resolved by the stored answer label as well, so
// submissions saved before `insight` existed still show a value.
const Q4_OPTIONS =
  (quiz.sections || [])
    .flatMap((sec) => sec.questions || [])
    .find((q) => q.id === 4)?.options || [];

const dropoutReasonOf = (answers) => {
  const picked = (answers || []).find((a) => a.id === 4)?.answer;
  if (!picked) return "";
  return (
    picked.insight ||
    Q4_OPTIONS.find((o) => o.label === picked.label)?.insight ||
    ""
  );
};

const QUESTIONS_BY_ID = Object.fromEntries(
  (quiz.sections || [])
    .flatMap((sec) => sec.questions || [])
    .map((q) => [q.id, q]),
);

// Submissions coming back from HubSpot carry only the chosen answer label, so
// the question copy, per-option score and insight are resolved here against
// Quiz.json — the single source for question content. Anything the submission
// already carries wins, so file-backed submissions are left untouched.
const enrichAnswers = (answers) =>
  (answers || []).map((a) => {
    const q = QUESTIONS_BY_ID[a.id];
    if (!q) return a;

    const withCopy = {
      ...a,
      title: a.title || q.title,
      question: a.question || q.content,
    };

    if (isGrouped(a.answer)) {
      const answer = {};
      Object.keys(a.answer).forEach((k) => {
        const picked = a.answer[k] || {};
        const option = (q.groups?.[k]?.options || []).find(
          (o) => o.label === picked.label,
        );
        answer[k] = { ...option, ...picked };
      });
      return { ...withCopy, answer };
    }

    const option = (q.options || []).find((o) => o.label === a.answer?.label);
    return { ...withCopy, answer: { ...option, ...a.answer } };
  });

// Lead-score ranges for the filter dropdown (matches the grade bands).
const SCORE_RANGES = [
  { value: "75-100", label: "75 - 100", min: 75, max: 100 },
  { value: "55-74", label: "55 - 74", min: 55, max: 74 },
  { value: "35-54", label: "35 - 54", min: 35, max: 54 },
  { value: "0-34", label: "Under 35", min: 0, max: 34 },
];

// US state abbreviation → full name (zip-derived `state` is stored as e.g. "FL").
const US_STATES = {
  AL: "Alabama", AK: "Alaska", AZ: "Arizona", AR: "Arkansas", CA: "California",
  CO: "Colorado", CT: "Connecticut", DE: "Delaware", DC: "District of Columbia",
  FL: "Florida", GA: "Georgia", HI: "Hawaii", ID: "Idaho", IL: "Illinois",
  IN: "Indiana", IA: "Iowa", KS: "Kansas", KY: "Kentucky", LA: "Louisiana",
  ME: "Maine", MD: "Maryland", MA: "Massachusetts", MI: "Michigan",
  MN: "Minnesota", MS: "Mississippi", MO: "Missouri", MT: "Montana",
  NE: "Nebraska", NV: "Nevada", NH: "New Hampshire", NJ: "New Jersey",
  NM: "New Mexico", NY: "New York", NC: "North Carolina", ND: "North Dakota",
  OH: "Ohio", OK: "Oklahoma", OR: "Oregon", PA: "Pennsylvania",
  RI: "Rhode Island", SC: "South Carolina", SD: "South Dakota", TN: "Tennessee",
  TX: "Texas", UT: "Utah", VT: "Vermont", VA: "Virginia", WA: "Washington",
  WV: "West Virginia", WI: "Wisconsin", WY: "Wyoming", PR: "Puerto Rico",
};
const stateName = (abbr) => {
  if (!abbr) return "";
  return US_STATES[String(abbr).trim().toUpperCase()] || abbr;
};

// A single answer can be a plain option {label} or, for the grouped readiness
// question (Q7), an object keyed by group index. Flatten either to a string.
const answerText = (a) => {
  if (!a) return "N/A";
  if (a.label) return a.label;
  const vals = Object.values(a)
    .filter((v) => v && v.label)
    .map((v) => v.label);
  return vals.length ? vals.join(" · ") : "N/A";
};

export default function AdminAssessments() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [cityFilter, setCityFilter] = useState("all");
  const [scoreFilter, setScoreFilter] = useState("all");
  const [sortKey, setSortKey] = useState("date"); // active column: "date" | "score"
  const [dateDir, setDateDir] = useState("desc"); // each column keeps its own dir
  const [scoreDir, setScoreDir] = useState("desc");
  const [page, setPage] = useState(1);
  const [viewing, setViewing] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setError("");
    adminListQuizSubmissions()
      .then(setRows)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const stats = useMemo(() => {
    const byGrade = { A: 0, B: 0, C: 0, D: 0 };
    let scoreSum = 0;
    rows.forEach((r) => {
      const g = (r.grade || "").toUpperCase();
      if (byGrade[g] != null) byGrade[g] += 1;
      scoreSum += Number(r.score?.total || 0);
    });
    const total = rows.length;
    return {
      total,
      avg: total ? Math.round(scoreSum / total) : 0,
      high: byGrade.A + byGrade.B,
      low: byGrade.C + byGrade.D,
      byGrade,
    };
  }, [rows]);

  // Roles: full master list from the quiz config, plus any extra roles that
  // somehow appear in the data but aren't in the master list.
  const roleOptions = useMemo(() => {
    const master = (quiz.form?.roleOptions || []).map((r) => r.trim()).filter(Boolean);
    const fromData = rows.map((r) => (r.details?.role || "").trim()).filter(Boolean);
    return [...new Set([...master, ...fromData])];
  }, [rows]);
  const cityOptions = useMemo(
    () =>
      [...new Set(rows.map((r) => (r.details?.city || "").trim()).filter(Boolean))].sort(),
    [rows],
  );

  const filtered = rows.filter((r) => {
    const d = r.details || {};
    if (roleFilter !== "all" && (d.role || "") !== roleFilter) return false;
    if (cityFilter !== "all" && (d.city || "") !== cityFilter) return false;
    if (scoreFilter !== "all") {
      const range = SCORE_RANGES.find((s) => s.value === scoreFilter);
      const total = Number(r.score?.total ?? 0);
      if (range && (total < range.min || total > range.max)) return false;
    }
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return [
      `${d.firstName || ""} ${d.lastName || ""}`,
      d.practiceName,
      d.workEmail,
      d.role,
      d.state,
      stateName(d.state),
      d.city,
      r.score?.total != null ? String(r.score.total) : "",
    ].some((v) => (v || "").toLowerCase().includes(q));
  });

  useEffect(() => {
    setPage(1);
  }, [query, roleFilter, cityFilter, scoreFilter]);

  // Each column keeps its own direction. Sort by whichever column is active.
  const dirFor = (key) => (key === "score" ? scoreDir : dateDir);
  const sorted = [...filtered].sort((a, b) => {
    let va, vb;
    if (sortKey === "score") {
      va = Number(a.score?.total ?? 0);
      vb = Number(b.score?.total ?? 0);
    } else {
      va = new Date(a.createdAt || 0).getTime();
      vb = new Date(b.createdAt || 0).getTime();
    }
    return dirFor(sortKey) === "asc" ? va - vb : vb - va;
  });

  // Click a header: if it's already the active column, flip its own direction;
  // otherwise make it active (keeping the direction it already shows).
  const toggleSort = (key) => {
    if (sortKey === key) {
      const setDir = key === "score" ? setScoreDir : setDateDir;
      setDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
    }
  };
  // Every sortable column always shows its own ▼/▲ direction.
  const sortGlyph = (key) => (dirFor(key) === "desc" ? "▼" : "▲");

  const totalPages = Math.max(1, Math.ceil(sorted.length / PER_PAGE));
  const currentPage = Math.min(page, totalPages);
  const pageRows = sorted.slice(
    (currentPage - 1) * PER_PAGE,
    currentPage * PER_PAGE,
  );

  // Detail view replaces the table when a row is opened.
  if (viewing) {
    return <AssessmentDetail submission={viewing} onBack={() => setViewing(null)} />;
  }

  return (
    <div className={styles.wrap}>
      {!loading && !error && (
        <div className={styles.statsGrid}>
          <div className={`${styles.statCard} ${styles.stTeal}`}>
            <div className={styles.statTop}>
              <div>
                <div className={styles.statValue}>{stats.total}</div>
                <div className={styles.statLabel}>Total Submissions</div>
              </div>
              <span className={styles.statIcon}><IconStack /></span>
            </div>
            <div className={styles.statFoot}>All practice assessments</div>
          </div>

          <div className={`${styles.statCard} ${styles.stGreen}`}>
            <div className={styles.statTop}>
              <div>
                <div className={styles.statValue}>{stats.high}</div>
                <div className={styles.statLabel}>High Intent</div>
              </div>
              <span className={styles.statIcon}><IconFlame /></span>
            </div>
            <div className={styles.statFoot}>
              Hot {stats.byGrade.A} · Warm {stats.byGrade.B}
            </div>
          </div>

          <div className={`${styles.statCard} ${styles.stRose}`}>
            <div className={styles.statTop}>
              <div>
                <div className={styles.statValue}>{stats.low}</div>
                <div className={styles.statLabel}>Low Intent</div>
              </div>
              <span className={styles.statIcon}><IconTrendDown /></span>
            </div>
            <div className={styles.statFoot}>
              Develop {stats.byGrade.C} · Low {stats.byGrade.D}
            </div>
          </div>

          <div className={`${styles.statCard} ${styles.stAmber}`}>
            <div className={styles.statTop}>
              <div>
                <div className={styles.statValue}>{stats.avg}<span className={styles.statUnit}>/100</span></div>
                <div className={styles.statLabel}>Average Score</div>
              </div>
              <span className={styles.statIcon}><IconGauge /></span>
            </div>
            <div className={styles.statFoot}>Across all submissions</div>
          </div>
        </div>
      )}

      <div className={styles.filterRow}>
        <input
          className={`${styles.search} ${styles.searchInRow}`}
          placeholder="Search by name, practice, email…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <select
          className={styles.statusSelect}
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
        >
          <option value="all">All Specialities</option>
          {roleOptions.map((r) => (
            <option key={r} value={r}>{r}</option>
          ))}
        </select>
        <select
          className={styles.statusSelect}
          value={cityFilter}
          onChange={(e) => setCityFilter(e.target.value)}
        >
          <option value="all">All Cities</option>
          {cityOptions.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        <select
          className={styles.statusSelect}
          value={scoreFilter}
          onChange={(e) => setScoreFilter(e.target.value)}
        >
          <option value="all">All Lead Scores</option>
          {SCORE_RANGES.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className={styles.spinnerWrap}>
          <span className={styles.spinner} />
          <span>Loading submissions…</span>
        </div>
      ) : error ? (
        <div className={styles.error}>{error}</div>
      ) : (
        <>
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th className={styles.idxCol}>#</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Practice</th>
                  <th>Speciality</th>
                  <th>Location</th>
                  <th>
                    <button
                      type="button"
                      className={styles.sortHeader}
                      onClick={() => toggleSort("score")}
                      title="Sort by lead score"
                    >
                      Lead Score
                      <span className={`${styles.sortArrow} ${sortKey === "score" ? styles.sortArrowActive : ""}`}>
                        {sortGlyph("score")}
                      </span>
                    </button>
                  </th>
                  <th>
                    <button
                      type="button"
                      className={styles.sortHeader}
                      onClick={() => toggleSort("date")}
                      title="Sort by date"
                    >
                      Date
                      <span className={`${styles.sortArrow} ${sortKey === "date" ? styles.sortArrowActive : ""}`}>
                        {sortGlyph("date")}
                      </span>
                    </button>
                  </th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.length === 0 ? (
                  <tr>
                    <td colSpan={9} className={styles.tableEmpty}>
                      No Assessment found
                    </td>
                  </tr>
                ) : (
                  pageRows.map((r, i) => {
                  const d = r.details || {};
                  const name = `${d.firstName || ""} ${d.lastName || ""}`.trim() || "N/A";
                  const loc = [d.city, stateName(d.state)].filter(Boolean).join(", ") || "N/A";
                  const grade = (r.grade || "").toUpperCase();
                  const gradeCls = styles[`gr_${grade.toLowerCase()}`] || "";
                  return (
                    <tr key={r.id}>
                      <td className={styles.idxCol}>
                        {(currentPage - 1) * PER_PAGE + i + 1}
                      </td>
                      <td className={styles.fileName}>{name}</td>
                      <td>{d.workEmail || "N/A"}</td>
                      <td>{d.practiceName || "N/A"}</td>
                      <td>{d.role || "N/A"}</td>
                      <td>{loc}</td>
                      <td>
                        <span className={`${styles.leadScore} ${gradeCls}`}>
                          {r.score?.total ?? 0}
                        </span>
                      </td>
                      <td>{fmtDateTime(r.createdAt)}</td>
                      <td>
                        <button
                          type="button"
                          className={styles.viewBtn}
                          aria-label={`View assessment for ${name}`}
                          title="View assessment"
                          onClick={() => setViewing(r)}
                        >
                          <EyeIcon />
                          {/* <span>View</span> */}
                        </button>
                      </td>
                    </tr>
                  );
                  })
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className={styles.pagination}>
              <button
                className={styles.pageBtn}
                disabled={currentPage <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                ← Prev
              </button>
              <span className={styles.pageInfo}>
                Page {currentPage} of {totalPages}
              </span>
              <button
                className={styles.pageBtn}
                disabled={currentPage >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Next →
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function AssessmentDetail({ submission, onBack }) {
  const d = submission.details || {};
  const s = submission.score || {};
  const bd = s.breakdown || {};
  const name = `${d.firstName || ""} ${d.lastName || ""}`.trim() || "N/A";
  const grade = (submission.grade || "").toUpperCase();
  const answers = enrichAnswers(submission.answers);
  const gradeColor = GRADE_COLOR[grade] || "#8A857A";
  const gradeWord = GRADE_WORD[grade] || "";

  // Fit segments (Size + Pain) and Intent segments (Stage + Openness + Readiness).
  // Older submissions have no breakdown — fall back to a single filled segment.
  const fitTotal = Number(s.fit || 0);
  const intentTotal = Number(s.intent || 0);
  const size = bd.size != null ? bd.size : fitTotal;
  const pain = bd.pain != null ? bd.pain : 0;
  const stage = bd.q5 != null ? bd.q5 : intentTotal;
  const openness = bd.q6 != null ? bd.q6 : 0;
  const readiness = bd.q7 != null ? bd.q7 : 0;

  // Lead insights — backend scoring will populate these; fall back to "N/A".
  const insights = submission.insights || {};
  const insightBoxes = [
    { label: "Role", value: d.role || insights.role },
    { label: "Dropout Reason", value: insights.dropoutReason || dropoutReasonOf(answers) },
    { label: "Current Brand", value: insights.currentBrand },
    { label: "EDOF Familiarity", value: insights.edofFamiliarity },
    { label: "Eligibility", value: insights.eligibility },
  ];

  // When the detail opens, reset the surrounding scroll area to the top so the
  // view never starts mid-page (it shares the admin .main scroll container).
  const wrapRef = useRef(null);
  useEffect(() => {
    let el = wrapRef.current?.parentElement;
    while (el && el !== document.body) {
      const oy = window.getComputedStyle(el).overflowY;
      if ((oy === "auto" || oy === "scroll") && el.scrollHeight > el.clientHeight) {
        el.scrollTop = 0;
      }
      el = el.parentElement;
    }
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className={styles.wrap} ref={wrapRef}>
      <button type="button" className={styles.backBtn} onClick={onBack}>
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <line x1="19" y1="12" x2="5" y2="12" />
          <polyline points="12 19 5 12 12 5" />
        </svg>
        Back
      </button>

      <div className={styles.detailHead}>
        <div>
          <div className={styles.pageTitle}>{name}</div>
        </div>
      </div>

      <div className={styles.detailLayout}>
        {/* LEFT — questions & answers */}
        <div className={styles.panel}>
          <div className={styles.panelTitle}>Questions &amp; Answers</div>
          <div className={styles.qaList}>
            {answers.map((a, i) => (
              <div key={i} className={styles.qaRow}>
                <div className={styles.qaKicker}>{a.title}</div>
                {a.question && <div className={styles.qaQuestion}>{a.question}</div>}
                {isGrouped(a.answer) ? (
                  <>
                    {submission.fittingSet && (
                      <div className={`${styles.qaBox} ${styles.qaBoxFull}`}>
                        <div className={styles.qaBoxLabel}>Fitting Set Interest</div>
                        <div className={styles.qaBoxValue}>
                          {submission.fittingSet.label}
                        </div>
                      </div>
                    )}
                    <div className={styles.qaBoxes}>
                      {Object.keys(a.answer)
                        .sort((x, y) => Number(x) - Number(y))
                        .map((k) => (
                          <div key={k} className={styles.qaBox}>
                            <div className={styles.qaBoxLabel}>
                              {READINESS_GROUPS[k] || `Part ${Number(k) + 1}`}
                            </div>
                            <div className={styles.qaBoxValue}>
                              {a.answer[k]?.label || "N/A"}
                            </div>
                          </div>
                        ))}
                    </div>
                  </>
                ) : (
                  <div className={styles.qaAnswer}>{answerText(a.answer)}</div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* RIGHT — score visual (dial + grade + Fit/Intent bars) */}
        <div>
          <div className={styles.scoreCard}>
            <div className={styles.scoreTop}>
              <div
                className={styles.dial}
                style={{ "--pct": String(s.total || 0), "--gradeC": gradeColor }}
              >
                <div className={styles.dialNum}>{s.total ?? 0}</div>
                <div className={styles.dialDen}>/ 100</div>
              </div>
              <div>
                <span className={styles.scorePill} style={{ background: gradeColor }}>
                  Grade {grade || "N/A"}
                </span>
                <div className={styles.gradeWord}>{gradeWord}</div>
              </div>
            </div>

            <div className={styles.barRow}>
              <div className={styles.barMeta}>
                <span>Fit</span>
                <span className={styles.barV}>{s.fit ?? 0} / 60</span>
              </div>
              <div className={styles.track}>
                <div className={`${styles.seg} ${styles.segA}`} style={{ width: pct(size, 60) }} />
                <div className={`${styles.seg} ${styles.segC}`} style={{ width: pct(pain, 60) }} />
              </div>
              <div className={styles.legend}>
                <span><i className={styles.legA} />Size {size}</span>
                <span><i className={styles.legC} />Pain {pain}</span>
              </div>
            </div>

            <div className={styles.barRow}>
              <div className={styles.barMeta}>
                <span>Intent</span>
                <span className={styles.barV}>{s.intent ?? 0} / 40</span>
              </div>
              <div className={styles.track}>
                <div className={`${styles.seg} ${styles.segA}`} style={{ width: pct(stage, 40) }} />
                <div className={`${styles.seg} ${styles.segB}`} style={{ width: pct(openness, 40) }} />
                <div className={`${styles.seg} ${styles.segC}`} style={{ width: pct(readiness, 40) }} />
              </div>
              <div className={styles.legend}>
                <span><i className={styles.legA} />Stage {stage}</span>
                <span><i className={styles.legB} />Openness {openness}</span>
                <span><i className={styles.legC} />Readiness {readiness}</span>
              </div>
            </div>

            {/* Lead insights — inside the score card, below the bars */}
            <div className={styles.insightGrid}>
              {insightBoxes.map((b) => (
                <div key={b.label} className={styles.insightBox}>
                  <div className={styles.qaBoxLabel}>{b.label}</div>
                  <div className={styles.qaBoxValue}>{b.value || "N/A"}</div>
                </div>
              ))}
            </div>
          </div>

          <div className={styles.panel} style={{ marginTop: "1.25rem" }}>
            <div className={styles.panelTitle}>Details</div>
            <div className={styles.detailGrid}>
              <Detail label="Email" value={d.workEmail} />
              <Detail label="NPI" value={d.npi} />
              <Detail label="ZIP" value={d.zipCode} />
              <Detail label="Location" value={[d.city, stateName(d.state)].filter(Boolean).join(", ")} />
              <Detail label="Date" value={fmtDateTime(submission.createdAt)} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div>
      <div className={styles.detailLabel}>{label}</div>
      <div className={styles.detailValue}>{value || "N/A"}</div>
    </div>
  );
}
